/**
 * Error Parser - parses various error types into AppError
 */

import i18next from "i18next";
import axios, { AxiosError } from "axios";
import {
  ErrorType,
  AppError,
  ERROR_MESSAGES,
  getErrorCategory,
  isRetryableError,
  requiresReauth,
} from "./types";

export function parseError(
  error: unknown,
  context?: Record<string, unknown>,
): AppError {
  // Axios error with response
  if (axios.isAxiosError(error)) {
    return parseAxiosError(error, context);
  }

  // Network error (no response)
  if (error instanceof TypeError && error.message.includes("fetch")) {
    return createError("NO_INTERNET", "Network request failed", context);
  }

  // Timeout
  if (error instanceof Error && error.message.includes("timeout")) {
    return createError("TIMEOUT", "Request timeout", context);
  }

  // Regular Error
  if (error instanceof Error) {
    return parseErrorMessage(error.message, context);
  }

  // Unknown
  return createError("UNKNOWN", String(error), context);
}

function parseAxiosError(
  error: AxiosError,
  context?: Record<string, unknown>,
): AppError {
  const response = error.response;
  const status = response?.status;
  const data = response?.data as Record<string, unknown> | undefined;

  // Get error code from response
  const errorCode =
    (data?.code as string | undefined) ||
    (data?.error_code as string | undefined) ||
    (data?.error as string | undefined);

  // Get error message from response
  const errorMessage =
    (data?.message as string | undefined) ||
    (data?.detail as string | undefined) ||
    error.message;

  if (!status) {
    // Check if request was cancelled (AbortController / axios CanceledError)
    if (axios.isCancel(error) || error.message === "canceled" || error.name === "CanceledError") {
      return createError("REQUEST_CANCELLED", errorMessage, context);
    }
    // Network error (no response)
    if (error.message.includes("Network Error")) {
      return createError("NO_INTERNET", errorMessage, context);
    }
    if (error.message.includes("timeout")) {
      return createError("TIMEOUT", errorMessage, context);
    }
    return createError("NETWORK_ERROR", errorMessage, context);
  }

  // Parse HTTP status
  return parseHttpStatus(status, errorCode, errorMessage, context);
}

function toStr(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function parseHttpStatus(
  status: number,
  errorCode?: string,
  errorMessage?: string,
  context?: Record<string, unknown>,
): AppError {
  const code = toStr(errorCode);
  const msg = toStr(errorMessage);

  switch (status) {
    case 400:
      if (code.toLowerCase().includes("validation")) {
        return createError("VALIDATION_ERROR", msg, context);
      }
      return createError("BAD_REQUEST", msg, context);

    case 401:
      if (
        code.toLowerCase().includes("token") ||
        msg.toLowerCase().includes("token")
      ) {
        if (msg.toLowerCase().includes("expired")) {
          return createError("TOKEN_EXPIRED", msg, context);
        }
        return createError("INVALID_TOKEN", msg, context);
      }
      if (
        msg.toLowerCase().includes("invalid") &&
        msg.toLowerCase().includes("credential")
      ) {
        return createError("INVALID_CREDENTIALS", msg, context);
      }
      return createError("UNAUTHORIZED", msg, context);

    case 403:
      return createError("FORBIDDEN", msg, context);

    case 404:
      return createError("NOT_FOUND", msg, context);

    case 409:
      if (
        msg.toLowerCase().includes("exists") ||
        msg.toLowerCase().includes("duplicate")
      ) {
        return createError("DUPLICATE_ENTRY", msg, context);
      }
      return createError("CONFLICT", msg, context);

    case 422:
      return createError("VALIDATION_ERROR", msg, context);

    case 429:
      return createError(
        "LIMIT_EXCEEDED",
        "Too many requests. Please try again later.",
        context,
      );

    case 500:
      return createError("SERVER_ERROR", msg, context);

    case 502:
    case 503:
      return createError("SERVICE_UNAVAILABLE", msg, context);

    case 504:
      return createError("TIMEOUT", msg, context);

    default:
      if (status >= 500) {
        return createError("SERVER_ERROR", msg, context);
      }
      if (status >= 400) {
        return createError("BAD_REQUEST", msg, context);
      }
      return createError("UNKNOWN", msg, context);
  }
}

function parseErrorMessage(
  message: string,
  context?: Record<string, unknown>,
): AppError {
  const msg = message.toLowerCase();

  if (
    msg.includes("network") ||
    msg.includes("fetch") ||
    msg.includes("connection")
  ) {
    return createError("NO_INTERNET", message, context);
  }
  if (msg.includes("timeout")) {
    return createError("TIMEOUT", message, context);
  }
  if (msg.includes("unauthorized") || msg.includes("token")) {
    if (msg.includes("expired")) {
      return createError("TOKEN_EXPIRED", message, context);
    }
    return createError("UNAUTHORIZED", message, context);
  }
  if (msg.includes("parse") || msg.includes("json")) {
    return createError("PARSE_ERROR", message, context);
  }

  return createError("UNKNOWN", message, context);
}

const ERROR_I18N_KEYS: Partial<Record<ErrorType, string>> = {
  UNAUTHORIZED: "error_unauthorized",
  FORBIDDEN: "error_forbidden",
  NOT_FOUND: "error_http_404",
  BAD_REQUEST: "error_http_400",
  CONFLICT: "error_http_409",
  VALIDATION_ERROR: "error_validation",
  SERVER_ERROR: "error_server",
  SERVICE_UNAVAILABLE: "error_http_502",
  TIMEOUT: "error_timeout",
  NETWORK_ERROR: "error_network",
  NO_INTERNET: "error_network_message",
  TOKEN_EXPIRED: "error_token_expired",
  INVALID_TOKEN: "error_token_invalid",
  INVALID_CREDENTIALS: "error_invalid_credentials",
  INVALID_EMAIL: "error_invalid_email",
  WEAK_PASSWORD: "error_weak_password",
  DUPLICATE_ENTRY: "error_email_already_exists",
  LIMIT_EXCEEDED: "error_default",
  UNKNOWN: "error_unknown",
};

function createError(
  type: ErrorType,
  message: string | undefined,
  context?: Record<string, unknown>,
): AppError {
  const i18nKey = ERROR_I18N_KEYS[type];
  const userMessage = i18nKey ? i18next.t(i18nKey) : (ERROR_MESSAGES[type] || message || "Unknown error");

  return {
    type,
    message: message || "Unknown error",
    userMessage,
    category: getErrorCategory(type),
    timestamp: new Date(),
    isRetryable: isRetryableError(type),
    requiresReauth: requiresReauth(type),
    context,
  };
}

export function parseValidationErrors(
  errors: Record<string, string[]>,
): Record<string, string> {
  const result: Record<string, string> = {};

  for (const [field, messages] of Object.entries(errors)) {
    if (messages.length > 0) {
      result[field] = messages[0];
    }
  }

  return result;
}

export function isAuthError(error: AppError): boolean {
  return error.requiresReauth;
}

export function isNetworkError(error: AppError): boolean {
  return error.category === "network";
}

export function isValidationError(error: AppError): boolean {
  return error.category === "validation";
}
